import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@hortis/ui/button";
import { FormControl, FormField, FormItem, FormMessage } from "@hortis/ui/form";
import { ChevronDown } from "@hortis/ui/icons";
import { Tag } from "@hortis/ui/tag";
import * as A from "fp-ts/Array";
import type { NullableObservationFilter } from "generated/graphql";
import { ObservationConditionType } from "generated/graphql";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { ObservationConditionMultiPicker } from "src/components/pickers/observation-condition-picker/observation-condition-picker";
import {
  CollapsableRadioButton,
  RadioButton,
  RadioGroup,
} from "src/components/radio";
import { FilterTitle } from "src/features/filters/components/modal/filter-title";
import { ObservationConditionRating } from "src/features/material-page/sections/material-record-observations/observation-condition";
import { observationConditionLabelMap } from "src/utils/label-maps/observation-condition";
import { twMerge } from "tailwind-merge";
import { match } from "ts-pattern";
import { z } from "zod";
import type { FilterModalProps } from "../../components/modal/filter-modal";
import { FilterModal } from "../../components/modal/filter-modal";
import { FilterModalButtons } from "../../components/modal/modal-buttons";
import { FilterModalError } from "../../components/modal/modal-error";
import { useFilterForm } from "../../components/modal/use-filter-form";
import type { FilterFormProps } from "../modals/types";
import { useDispatchFilter } from "../modals/use-dispatch-filter";

const createDefaultValues = (
  initialValue?: NonNullable<NullableObservationFilter>,
) => {
  const defaultValues = {
    is:
      initialValue?.condition?.or == null
        ? []
        : initialValue.condition.or
            .flatMap(({ eq }) => eq)
            .filter((val) => val != null),
    isNot:
      initialValue?.condition?.or == null
        ? []
        : initialValue.condition?.or
            .flatMap(({ neq }) => neq)
            .filter((val) => val != null),
    isOutdated: initialValue?.outdated?.eq ?? null,
    hasNoObservation: initialValue?.null?.eq ?? null,
  } as const;
  return {
    defaultValues,
    initialOperation:
      defaultValues.is.length > 0
        ? "is"
        : defaultValues.isNot.length > 0
          ? "isNot"
          : defaultValues.isOutdated != null
            ? "isOutdated"
            : defaultValues.hasNoObservation != null
              ? "hasNoObservation"
              : "is",
  };
};

const schema = z.object({
  is: z.array(z.nativeEnum(ObservationConditionType)),
  isNot: z.array(z.nativeEnum(ObservationConditionType)),
  isOutdated: z.boolean().nullish(),
  hasNoObservation: z.boolean().nullish(),
});

export const ObservationConditionFilterForm = ({
  initialValue,
  onClose,
  dispatchFilters,
}: FilterFormProps) => {
  const { error, setError, operation, handleRadioChange, formMethods } =
    useFilterForm({
      resolver: zodResolver(schema),
      ...createDefaultValues(initialValue?.lastObservation ?? undefined),
    });
  const { addModifyFilter, deleteFilter } = useDispatchFilter({
    dispatchFilters,
    id: initialValue?.id,
    onClose,
  });

  const onSubmit = useMemo(
    () =>
      formMethods.handleSubmit((values) => {
        match(operation)
          .with("is", () => {
            if (A.isNonEmpty(values.is)) {
              addModifyFilter({
                lastObservation: {
                  condition: {
                    or: values.is.map((val) => ({
                      eq: val,
                    })),
                  },
                },
              });
              onClose();
            } else {
              setError("Select a condition to continue");
            }
          })
          .with("isNot", () => {
            if (A.isNonEmpty(values.isNot)) {
              addModifyFilter({
                lastObservation: {
                  condition: {
                    or: values.isNot.map((val) => ({
                      neq: val,
                    })),
                  },
                },
              });
              onClose();
            } else {
              setError("Select a condition to continue");
            }
          })
          .with("isOutdated", () => {
            addModifyFilter({
              lastObservation: {
                outdated: {
                  eq: true,
                },
              },
            });
            onClose();
          })
          .with("hasNoObservation", () => {
            addModifyFilter({
              lastObservation: {
                null: {
                  eq: true,
                },
              },
            });
            onClose();
          })
          .run();
      }),
    [formMethods, operation, onClose, addModifyFilter, setError],
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit}>
        <div className="mb-4 flex flex-col gap-4">
          <FilterTitle title="Condition" deleteFilter={deleteFilter} />
          <FilterModalError error={error} />
          <RadioGroup
            value={operation}
            onChange={handleRadioChange}
            label="Operation"
          >
            {(props) => (
              <>
                <CollapsableRadioButton
                  selectedValue="is"
                  testId="condition-is-radio"
                  label="Is"
                  {...props}
                >
                  <FormField
                    control={formMethods.control}
                    name="is"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ObservationConditionMultiPicker
                            value={field.value}
                            onChange={field.onChange}
                          >
                            <Button
                              className={twMerge(
                                "min-h-[44px] w-full justify-between truncate px-[14px]! py-2! text-grey-500",
                              )}
                              endIcon={ChevronDown}
                              data-cy="condition-is-picker"
                            >
                              {field.value == null ||
                              field.value.length === 0 ? (
                                "Select condition..."
                              ) : (
                                <div className="flex flex-wrap items-center gap-2 truncate">
                                  {field.value.map((condition) => (
                                    <Tag
                                      key={condition}
                                      data-cy="condition-picker-tag"
                                    >
                                      <ObservationConditionRating
                                        condition={condition}
                                      />
                                      {observationConditionLabelMap[condition]}
                                    </Tag>
                                  ))}
                                </div>
                              )}
                            </Button>
                          </ObservationConditionMultiPicker>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsableRadioButton>
                <CollapsableRadioButton
                  selectedValue="isNot"
                  testId="condition-is-not-radio"
                  label="Is not"
                  {...props}
                >
                  <FormField
                    control={formMethods.control}
                    name="isNot"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <ObservationConditionMultiPicker
                            value={field.value}
                            onChange={field.onChange}
                            data-cy="condition-is-not-picker"
                          >
                            <Button
                              className={twMerge(
                                "min-h-[44px] w-full justify-between truncate px-[14px]! py-2! text-grey-500",
                              )}
                              endIcon={ChevronDown}
                              data-cy="condition-is-not-picker"
                            >
                              {field.value == null ||
                              field.value.length === 0 ? (
                                "Select condition..."
                              ) : (
                                <div className="flex flex-wrap items-center gap-2 truncate">
                                  {field.value.map((condition) => (
                                    <Tag
                                      key={condition}
                                      data-cy="condition-picker-tag"
                                    >
                                      <ObservationConditionRating
                                        condition={condition}
                                      />
                                      {observationConditionLabelMap[condition]}
                                    </Tag>
                                  ))}
                                </div>
                              )}
                            </Button>
                          </ObservationConditionMultiPicker>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsableRadioButton>
                <RadioButton
                  label="Is outdated"
                  selectedValue="isOutdated"
                  testId="condition-is-outdated-radio"
                  className="mb-5"
                  {...props}
                />
                <RadioButton
                  label="Has no observation"
                  selectedValue="hasNoObservation"
                  testId="condition-has-no-observation-radio"
                  className="mb-5"
                  {...props}
                />
              </>
            )}
          </RadioGroup>
        </div>
        <FilterModalButtons onCancel={onClose} testIdPrefix="condition" />
      </form>
    </FormProvider>
  );
};

// Seperated from form component to make sure popover unmount causes state reset
export const ObservationConditionFilterModal = ({
  open,
  onClose,
  anchorEl,
  ...props
}: FilterFormProps & FilterModalProps) => (
  <FilterModal anchorEl={anchorEl} open={open} onClose={onClose}>
    <ObservationConditionFilterForm onClose={onClose} {...props} />
  </FilterModal>
);
